import { forwardRef, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  loading?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      onBlur,
      placeholder = "Select...",
      label,
      error,
      required,
      disabled,
      clearable = false,
      searchable = false,
      loading = false,
      id: idProp,
      className,
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOption = useMemo(
      () => options.find((o) => o.value === value),
      [options, value],
    );

    const filtered = useMemo(() => {
      if (!search) return options;
      const lower = search.toLowerCase();
      return options.filter((o) => o.label.toLowerCase().includes(lower));
    }, [options, search]);

    function handleSelect(optValue: string) {
      onChange?.(optValue);
      setOpen(false);
      setSearch("");
    }

    function handleClear(e: React.MouseEvent) {
      e.stopPropagation();
      onChange?.("");
      setSearch("");
    }

    function handleToggle() {
      if (disabled || loading) return;
      const next = !open;
      setOpen(next);
      if (next && searchable) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    }

    function handleBlur(e: React.FocusEvent) {
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      setOpen(false);
      setSearch("");
      onBlur?.();
    }

    return (
      <div className={cn("space-y-1.5", className)} ref={containerRef} onBlur={handleBlur}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none text-foreground"
          >
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
        )}
        <div className="relative">
          <button
            ref={ref}
            type="button"
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            disabled={disabled || loading}
            onClick={handleToggle}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-destructive focus-visible:ring-destructive",
              !selectedOption && "text-muted-foreground",
            )}
          >
            <span className="truncate">
              {loading ? "Loading..." : selectedOption?.label ?? placeholder}
            </span>
            <div className="flex items-center gap-1">
              {clearable && value && !disabled && (
                <X
                  className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                  onClick={handleClear}
                />
              )}
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </button>

          {open && (
            <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover bg-white dark:bg-zinc-900 shadow-md animate-in fade-in-0 zoom-in-95">
              {searchable && (
                <div className="flex items-center border-b px-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search..."
                    className="flex h-9 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              )}
              <div className="max-h-60 overflow-y-auto p-1">
                {filtered.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No options found.
                  </div>
                ) : (
                  filtered.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        "flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
                        option.value === value && "bg-accent text-accent-foreground font-medium",
                      )}
                    >
                      {option.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        {error && (
          <p id={`${id}-error`} className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Select.displayName = "Select";

export { Select };
